/**
 * /learn (#777): an interactive explainer of HIP-4 outcome (prediction) contracts for newcomers.
 * Maths reuses the order ticket's functions (ticket/order.ts, trading/rules.ts, trading/fees.ts); the live example is
 * described by describe/ like the market page. Every factual statement is sourced in
 * docs/research/EDUCATION_SOURCES.md (items marked there for review are phrased cautiously here).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { builderConfigFor, feeToPercent } from '../builder/builder';
import { useEnv } from '../config/env';
import FeeBreakdown from '../learn/FeeBreakdown';
import PayoutCalculator from '../learn/PayoutCalculator';
import ProbabilitySlider from '../learn/ProbabilitySlider';
import SettlementSection from '../learn/SettlementSection';
import TouchExample from '../learn/TouchExample';
import TradingBasics from '../learn/TradingBasics';
import { evaluateCalc, type CalcState } from '../learn/calc';
import { useLearnExamples } from '../learn/useLearnExamples';
import { pxToCentsInput } from '../ticket/order';
import { toSidePrice } from '../trading/rules';
import { localizeNumber, usd } from '../i18n/format';
import type { PlainKey } from '../i18n/i18n';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

const SECTIONS = [
  { id: 'basics', title: 'learn.nav.basics' },
  { id: 'calculator', title: 'learn.nav.calculator' },
  { id: 'trading', title: 'learn.nav.trading' },
  { id: 'settlement', title: 'learn.nav.settlement' },
  { id: 'touch', title: 'learn.nav.touch' },
  { id: 'fees', title: 'learn.nav.fees' },
] as const satisfies readonly { id: string; title: PlainKey }[];

function Section({ id, n, title, intro, children }: { id: string; n: number; title: string; intro?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="card scroll-mt-28 space-y-3 p-4 sm:p-5">
      <h2 id={`${id}-h`} className="text-lg font-semibold">
        <span className="num me-2 text-accent">{n}.</span>
        {title}
      </h2>
      {intro && <div className="space-y-2 text-sm text-fg/85">{intro}</div>}
      {children}
    </section>
  );
}

export default function LearnPage() {
  const [yesCents, setYesCents] = useState(63);
  const [calc, setCalc] = useState<CalcState>({ side: 0, cents: '63', shares: '100' });
  const ex = useLearnExamples();
  const t = useT();
  const { env } = useEnv();
  // Builder codes are off unless configured (builder/builder.ts); the fee copy and breakdown follow the config.
  const builder = useMemo(() => builderConfigFor(env.name), [env.name]);
  const { hash } = useLocation();
  // In-app links such as /learn#trading (order ticket) land on their section.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.({ block: 'start' });
  }, [hash]);
  const evaluated = evaluateCalc(calc);

  // Moving the slider also prices the calculator's side (Yes p, No 1 - p), via the ticket's own helpers.
  const onSlider = (c: number) => {
    setYesCents(c);
    const yesPx = (c / 100).toFixed(2);
    setCalc((s) => ({ ...s, cents: pxToCentsInput(toSidePrice(yesPx, s.side)) }));
  };

  return (
    <article className="mx-auto max-w-3xl space-y-5" data-testid="learn-page">
      <header className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-accent">{t('nav.learn')}</p>
        <h1 className="text-2xl font-semibold sm:text-3xl">{t('learn.title')}</h1>
        <p className="text-sm text-fg/85">{t('learn.intro')}</p>
        <nav aria-label={t('learn.on_this_page')} className="flex flex-wrap gap-1.5 pt-1">
          {SECTIONS.map((s, i) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-full border border-border px-2.5 py-1 text-xs text-muted hover:border-fg/40 hover:text-fg"
            >
              {i + 1}. {t(s.title)}
            </a>
          ))}
        </nav>
      </header>

      <Section
        id="basics"
        n={1}
        title={t('learn.nav.basics')}
        intro={
          <>
            <p>
              <Msg
                k="learn.basics.p1"
                args={{
                  yes: <span className="text-yes">{t('common.yes')}</span>,
                  no: <span className="text-no">{t('common.no')}</span>,
                  one: <strong>{usd('1')}</strong>,
                  zero: <strong>{usd('0')}</strong>,
                }}
              />
            </p>
            <p>{t('learn.basics.p2')}</p>
          </>
        }
      >
        <ProbabilitySlider yesCents={yesCents} onChange={onSlider} />
      </Section>

      <Section
        id="calculator"
        n={2}
        title={t('learn.nav.calculator')}
        intro={<p>{t('learn.calc.intro')}</p>}
      >
        <PayoutCalculator state={calc} onChange={setCalc} />
      </Section>

      <Section id="trading" n={3} title={t('learn.nav.trading')}>
        <TradingBasics />
      </Section>

      <Section
        id="settlement"
        n={4}
        title={t('learn.section.settlement')}
        intro={<p>{t('learn.settlement.intro')}</p>}
      >
        <SettlementSection ex={ex} side={calc.side} shares={calc.shares} cost={evaluated.econ?.notional ?? null} />
      </Section>

      <Section
        id="touch"
        n={5}
        title={t('learn.nav.touch')}
        intro={
          <>
            <p data-testid="learn-touch-intro">
              <Msg
                k="learn.touch.p1"
                args={{
                  yesTouch: <strong>{t('learn.touch.p1_yes')}</strong>,
                  no: <strong>{t('common.no')}</strong>,
                  deadline: <strong>{t('describe.when.deadline')}</strong>,
                }}
              />
            </p>
            <p>{t('learn.touch.p2')}</p>
          </>
        }
      >
        <TouchExample />
      </Section>

      <Section
        id="fees"
        n={6}
        title={t('learn.nav.fees')}
        intro={
          <>
            <p>
              <Msg
                k="learn.fees.p1"
                args={{ close: <strong>{t('learn.fees.p1_close')}</strong>, noFee: <strong>{t('learn.fees.p1_no_fee')}</strong> }}
              />
            </p>
            <p>
              <Msg
                k="learn.fees.p2"
                args={{
                  docs: (
                    <a
                      href="https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/hip-4-deployer-actions#deployer-fee-scale"
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent underline"
                    >
                      {t('learn.fees.docs')}
                    </a>
                  ),
                }}
              />
            </p>
            <p data-testid="learn-builder-fee">
              <Msg
                k="learn.fees.p3"
                args={{
                  builderFee: <strong>{t('learn.fees.p3_builder_fee')}</strong>,
                  buysSells: <strong>{t('learn.fees.p3_buys_sells')}</strong>,
                }}
              />{' '}
              {builder
                ? t('learn.fees.builder_on', { pct: localizeNumber(feeToPercent(builder.fee)) })
                : t('learn.fees.builder_off')}
            </p>
          </>
        }
      >
        <FeeBreakdown
          side={calc.side}
          px={evaluated.econ ? evaluated.px : null}
          shares={calc.shares.trim()}
          exampleScale={ex.example ? (ex.example.meta.deployerFeeScale ?? null) : undefined}
          exampleTitle={ex.example?.desc.title}
          builder={builder}
        />
      </Section>

      <footer className="space-y-2 pb-4 text-xs text-muted">
        <p>{t('learn.footer.sources')}</p>
        <Link to="/" className="text-accent hover:underline">
          {t('learn.footer.browse')}
        </Link>
      </footer>
    </article>
  );
}
